/** @jsxImportSource @emotion/react */

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ConfigProvider, Skeleton } from "antd";
import { TransferApi } from "api/transfer";
import { TransferPointPayload } from "api/transfer/type";
import InputCustom from "components/Common/InputCustom";
import SelectCustom from "components/Common/SelectCustom";
import { ResponseCode } from "contants/response";
import { useAppSelector } from "hooks/app-hook";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { selectBalance, selectBalanceLoading } from "store/user";
import { getMemberBalance } from "store/user/user.thunk";
import { theme } from "styles/theme";
import { AppDispatch, StatusNotification } from "types";
import * as yup from "yup";

import { TransferPageStyle } from "./style";

interface Props {
  username: string;
  openNotification: (status: StatusNotification, message: string) => void;
  onTransferSuccess?: () => void;
}

interface TransferPointFormValues {
  username: string;
  selected_username: string;
  amount: number;
}

export const TransferPointCard = (props: Props) => {
  const { username, openNotification, onTransferSuccess } = props;
  const dispatch = useDispatch<AppDispatch>();
  const balance = useAppSelector(selectBalance);
  const isLoadingBalance = useAppSelector(selectBalanceLoading);
  const { t } = useTranslation([
    "transferPage",
    "button",
    "validate",
    "message",
  ]);
  const [playerList, setPlayerList] = useState<string[]>([]);
  const [playerType, setPlayerType] = useState<number | null>(null);
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  const schema = yup.object({
    username: yup.string().required(t("validate:FieldRequired")),
    selected_username: yup.string().required(t("validate:FieldRequired")),
    amount: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .required(t("validate:FieldRequired"))
      .moreThan(1, t("validate:AmountGreaterThanOne")),
  });

  const playerOptions = useMemo(
    () =>
      playerList.map((player) => ({
        label: player,
        value: player,
      })),
    [playerList]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<TransferPointFormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      username,
      amount: 0,
      selected_username: "",
    },
  });

  useEffect(() => {
    if (!username) {
      setPlayerList([]);
      return;
    }

    const getTransferPointForm = async () => {
      try {
        setIsLoadingForm(true);
        const res = await TransferApi.transferPointForm(username, { username });
        setIsLoadingForm(false);

        if (res.status === ResponseCode.SUCCESS) {
          setPlayerList(res.data?.list || []);
          setPlayerType(res.data?.player_type || null);
          return;
        }

        setPlayerList([]);
        openNotification("error", res.msg);
      } catch (error) {
        setIsLoadingForm(false);
        setPlayerList([]);
        openNotification("error", error as string);
      }
    };

    reset({
      username,
      amount: 0,
      selected_username: "",
    });
    getTransferPointForm();
  }, [openNotification, reset, username]);

  const selectedPlayer = watch("selected_username");
  const amount = Number(watch("amount")) || 0;
  const isDisabledSubmit =
    isLoadingForm ||
    isLoadingSubmit ||
    !selectedPlayer ||
    amount <= 1 ||
    !isValid;

  const onSubmit = async (data: TransferPointFormValues) => {
    try {
      setIsLoadingSubmit(true);
      const payload: TransferPointPayload = {
        username: username,
        to_username: data.selected_username,
        amount: data.amount,
      };
      const res = await TransferApi.transferPoint(username, payload);
      setIsLoadingSubmit(false);

      if (res.status === ResponseCode.SUCCESS) {
        openNotification("success", res.msg);
        dispatch(getMemberBalance({ username }));
        onTransferSuccess?.();
        reset({
          username,
          amount: 0,
          selected_username: "",
        });
      } else {
        openNotification("error", res.msg);
      }
    } catch (error) {
      setIsLoadingSubmit(false);
      openNotification("error", error as string);
    }
  };

  const mapPlayerName = (key: number) => {
    switch (key) {
      case 1:
        return t("Downline");
      case 2:
        return t("Upline");
      default:
        return "";
    }
  };

  return (
    <div css={TransferPageStyle.wrapInner}>
      <h3 className="title-heading">{t("TransferPointTitle")}</h3>
      <form css={TransferPageStyle.form} onSubmit={handleSubmit(onSubmit)}>
        <div className="box-transfer box-transfer-point">
          <div className="box-item">
            <Skeleton
              loading={isLoadingForm}
              style={{
                height: "80%",
                width: "60px",
                background: "#404040",
                marginBottom: "5px",
                borderRadius: "4px",
              }}
              title={isLoadingForm}
              active={isLoadingForm}
              paragraph={false}
              round
            >
              <h3 className="label">{mapPlayerName(playerType || 0)}</h3>
            </Skeleton>
            <SelectCustom
              control={control}
              name="selected_username"
              options={playerOptions}
              placeholder={
                playerOptions.length === 0
                  ? t("message:None")
                  : t("SelectPlayerPlaceholder")
              }
              error={errors.selected_username?.message}
              validate_status={errors.selected_username && "error"}
              loading={isLoadingForm}
              disabled={isLoadingForm || playerOptions.length === 0}
              className="transfer-point-player-select"
              showSearch
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
          <div className="box-item">
            <h3 className="label">{t("Cash")}</h3>
            <InputCustom
              control={control}
              name="amount"
              type="number"
              placeholder={t("EnterAmount")}
              min={0}
              errors={errors.amount?.message}
              validate_status={errors.amount && "error"}
              margin_bottom="0"
            />
            <div className="balance">
              <span>BDT:</span>
              {isLoadingBalance ? (
                <Skeleton
                  active
                  style={{ height: "15px", width: "50px" }}
                  paragraph={false}
                />
              ) : (
                <span className="value">{balance.total || "0.00"}</span>
              )}
            </div>
          </div>
        </div>
        <div css={TransferPageStyle.btnSubmit}>
          <ConfigProvider
            theme={{
              token: {
                colorBgContainerDisabled: theme.colors.gray[150],
                colorTextDisabled: theme.colors.white,
              },
            }}
          >
            <Button
              htmlType="submit"
              loading={isLoadingSubmit}
              disabled={isDisabledSubmit}
            >
              {t("button:Submit")}
            </Button>
          </ConfigProvider>
        </div>
      </form>
    </div>
  );
};
